import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineMap,
  HiOutlinePlus,
  HiOutlineEye,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineGlobe,
  HiOutlineDocumentDuplicate,
  HiOutlineExternalLink,
  HiOutlineSearch,
  HiOutlineLocationMarker,
  HiOutlineCode,
  HiOutlineX,
  HiOutlineClipboardCopy,
  HiOutlineCheck,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import mapService from '../services/mapService'

function MapList() {
  const navigate = useNavigate()
  const [maps, setMaps] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, draft, published
  const [searchQuery, setSearchQuery] = useState('')

  // Embed modal state
  const [showEmbedModal, setShowEmbedModal] = useState(false)
  const [embedMap, setEmbedMap] = useState(null)
  const [embedWidth, setEmbedWidth] = useState('100%')
  const [embedHeight, setEmbedHeight] = useState('500')
  const [embedCopied, setEmbedCopied] = useState(false)
  const [embedOptions, setEmbedOptions] = useState({
    hideHeader: false,
    hideSidebar: true,
    compact: true,
  })

  useEffect(() => {
    loadMaps()
  }, [filter])

  const loadMaps = async () => {
    setLoading(true)
    try {
      const params = filter !== 'all' ? { status: filter } : {}
      const response = await mapService.getMaps(params)
      if (response.status === 'success') {
        setMaps(response.data || [])
      }
    } catch (error) {
      console.error('Failed to load maps:', error)
      toast.error('Không thể tải danh sách maps')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateMap = () => {
    // Auto-generate map name based on user's existing map count
    const nextMapNumber = maps.length + 1
    const autoName = `Map BĐS #${nextMapNumber}`
    navigate('/map/create', { state: { autoName } })
  }

  const handleEditMap = (mapId) => {
    navigate(`/map/edit/${mapId}`)
  }

  const handleViewMap = (shareToken) => {
    window.open(`/map/view/${shareToken}`, '_blank')
  }

  const handleDeleteMap = async (mapId) => {
    if (!confirm('Bạn có chắc muốn xóa map này?')) return

    try {
      await mapService.deleteMap(mapId)
      toast.success('Đã xóa map')
      loadMaps()
    } catch (error) {
      console.error('Failed to delete map:', error)
      toast.error('Không thể xóa map')
    }
  }

  const handleCopyShareLink = async (shareToken) => {
    const url = mapService.getShareUrl(shareToken)
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Đã copy link chia sẻ!')
    } catch {
      toast.error('Không thể copy link')
    }
  }

  // Embed functions
  const handleOpenEmbed = (map) => {
    setEmbedMap(map)
    setEmbedWidth('100%')
    setEmbedHeight('500')
    setEmbedCopied(false)
    setEmbedOptions({
      hideHeader: false,
      hideSidebar: true,
      compact: true,
    })
    setShowEmbedModal(true)
  }

  const getEmbedUrl = () => {
    if (!embedMap) return ''
    const baseUrl = mapService.getShareUrl(embedMap.share_token)
    const params = new URLSearchParams()
    if (embedOptions.hideHeader) params.set('header', '0')
    if (embedOptions.hideSidebar) params.set('sidebar', '0')
    if (embedOptions.compact) params.set('compact', '1')
    const queryString = params.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }

  const getEmbedCode = () => {
    if (!embedMap) return ''
    const url = getEmbedUrl()
    const width = embedWidth.includes('%') ? embedWidth : `${embedWidth}px`
    const height = `${embedHeight}px`
    return `<iframe src="${url}" width="${width}" height="${height}" style="border:none;border-radius:12px;" allowfullscreen loading="lazy"></iframe>`
  }

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(getEmbedCode())
      setEmbedCopied(true)
      toast.success('Đã copy mã nhúng!')
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch {
      toast.error('Không thể copy')
    }
  }

  const handlePublish = async (mapId) => {
    try {
      const response = await mapService.publishMap(mapId)
      if (response.status === 'success') {
        toast.success('Map đã được xuất bản!')
        loadMaps()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể xuất bản')
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
            Đã xuất bản
          </span>
        )
      case 'draft':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            Nháp
          </span>
        )
      case 'archived':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-600">
            Đã lưu trữ
          </span>
        )
      default:
        return null
    }
  }

  // Filter maps by search
  const filteredMaps = maps.filter((map) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      map.name?.toLowerCase().includes(query) ||
      map.description?.toLowerCase().includes(query)
    )
  })

  // Count by status
  const statusCounts = {
    all: maps.length,
    draft: maps.filter((m) => m.status === 'draft').length,
    published: maps.filter((m) => m.status === 'published').length,
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mr-3">
              <HiOutlineMap className="w-5 h-5 text-white" />
            </div>
            Quản lý Map BĐS
          </h1>
          <p className="text-gray-500 mt-1 ml-13">
            Tạo và quản lý các map so sánh giá bất động sản
          </p>
        </div>

        <button
          onClick={handleCreateMap}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors shadow-lg shadow-primary-200"
        >
          <HiOutlinePlus className="w-5 h-5" />
          Tạo Map mới
        </button>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tìm kiếm map..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Filter tabs - đồng bộ với MapEditor/MapPublicView */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
          {[
            { value: 'all', label: 'Tất cả', count: statusCounts.all },
            { value: 'draft', label: 'Nháp', count: statusCounts.draft },
            { value: 'published', label: 'Đã xuất bản', count: statusCounts.published },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                filter === tab.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {tab.label}
              <span className="ml-1.5 text-xs text-gray-400">({tab.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Maps grid */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
              <div className="h-40 bg-gray-200" />
              <div className="p-4 space-y-3">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredMaps.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HiOutlineLocationMarker className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery ? 'Không tìm thấy map' : 'Chưa có map nào'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchQuery
              ? 'Thử tìm kiếm với từ khóa khác'
              : 'Tạo map đầu tiên để so sánh giá các dự án BĐS'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleCreateMap}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
            >
              <HiOutlinePlus className="w-5 h-5" />
              Tạo Map mới
            </button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMaps.map((map) => (
            <div
              key={map.id}
              className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-all group"
            >
              {/* Thumbnail */}
              <div className="relative h-40 bg-gradient-to-br from-primary-100 to-primary-200">
                {map.thumbnail_url ? (
                  <img
                    src={map.thumbnail_url}
                    alt={map.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <HiOutlineMap className="w-16 h-16 text-primary-300" />
                  </div>
                )}

                {/* Status badge */}
                <div className="absolute top-3 left-3">
                  {getStatusBadge(map.status)}
                </div>

                {/* View count */}
                {map.view_count > 0 && (
                  <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 rounded-full text-white text-xs flex items-center gap-1.5">
                    <HiOutlineEye className="w-3.5 h-3.5" />
                    {map.view_count.toLocaleString()}
                  </div>
                )}

                {/* Quick actions overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleEditMap(map.id)}
                    className="p-2.5 bg-white rounded-xl hover:bg-gray-100 transition-colors"
                    title="Chỉnh sửa"
                  >
                    <HiOutlinePencil className="w-5 h-5 text-gray-700" />
                  </button>
                  {map.status === 'published' && (
                    <button
                      onClick={() => handleViewMap(map.share_token)}
                      className="p-2.5 bg-white rounded-xl hover:bg-gray-100 transition-colors"
                      title="Xem map"
                    >
                      <HiOutlineExternalLink className="w-5 h-5 text-gray-700" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                  {map.name}
                </h3>
                {map.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {map.description}
                  </p>
                )}

                <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                  <span className="flex items-center gap-1">
                    <HiOutlineLocationMarker className="w-3.5 h-3.5" />
                    {map.marker_count || 0} markers
                  </span>
                  <span>•</span>
                  <span>
                    {new Date(map.updated_at).toLocaleDateString('vi-VN')}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditMap(map.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    <HiOutlinePencil className="w-4 h-4" />
                    Sửa
                  </button>

                  {map.status === 'published' ? (
                    <>
                      <button
                        onClick={() => handleViewMap(map.share_token)}
                        className="p-2 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                        title="Xem map"
                      >
                        <HiOutlineExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopyShareLink(map.share_token)}
                        className="p-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        title="Copy link"
                      >
                        <HiOutlineDocumentDuplicate className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenEmbed(map)}
                        className="p-2 rounded-xl bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                        title="Nhúng iframe"
                      >
                        <HiOutlineCode className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handlePublish(map.id)}
                      className="p-2 rounded-xl bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors"
                      title="Xuất bản"
                    >
                      <HiOutlineGlobe className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => handleDeleteMap(map.id)}
                    className="p-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    title="Xóa"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Embed Modal */}
      {showEmbedModal && embedMap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <HiOutlineCode className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Nhúng Map</h3>
                  <p className="text-xs text-gray-500">{embedMap.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowEmbedModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Size settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Chiều rộng
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={embedWidth}
                      onChange={(e) => setEmbedWidth(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="100% hoặc 600"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">VD: 100%, 800, 600px</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Chiều cao (px)
                  </label>
                  <input
                    type="number"
                    value={embedHeight}
                    onChange={(e) => setEmbedHeight(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="500"
                    min="200"
                    max="2000"
                  />
                </div>
              </div>

              {/* Quick size buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kích thước nhanh
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Tự động', w: '100%', h: '500' },
                    { label: 'Nhỏ', w: '400', h: '300' },
                    { label: 'Vừa', w: '600', h: '450' },
                    { label: 'Lớn', w: '800', h: '600' },
                    { label: 'Full HD', w: '1920', h: '1080' },
                  ].map((size) => (
                    <button
                      key={size.label}
                      onClick={() => {
                        setEmbedWidth(size.w)
                        setEmbedHeight(size.h)
                      }}
                      className={clsx(
                        'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                        embedWidth === size.w && embedHeight === size.h
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Display options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tùy chọn hiển thị
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={embedOptions.compact}
                      onChange={(e) => setEmbedOptions({ ...embedOptions, compact: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Giao diện thu gọn</span>
                    <span className="text-xs text-gray-400">(Khuyên dùng)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={embedOptions.hideSidebar}
                      onChange={(e) => setEmbedOptions({ ...embedOptions, hideSidebar: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Ẩn sidebar mặc định</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={embedOptions.hideHeader}
                      onChange={(e) => setEmbedOptions({ ...embedOptions, hideHeader: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Ẩn header</span>
                    <span className="text-xs text-gray-400">(Chỉ hiện map)</span>
                  </label>
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Xem trước
                </label>
                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                  <div
                    className="bg-white rounded-lg overflow-hidden shadow-sm mx-auto"
                    style={{
                      width: embedWidth.includes('%') ? '100%' : `min(${embedWidth}px, 100%)`,
                      height: `min(${Math.min(parseInt(embedHeight) || 200, 200)}px, 200px)`,
                    }}
                  >
                    <iframe
                      src={getEmbedUrl()}
                      className="w-full h-full border-0"
                      title="Preview"
                    />
                  </div>
                </div>
              </div>

              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mã nhúng HTML
                </label>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">
                    {getEmbedCode()}
                  </pre>
                  <button
                    onClick={handleCopyEmbed}
                    className={clsx(
                      'absolute top-2 right-2 p-2 rounded-lg transition-all',
                      embedCopied
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    )}
                    title="Copy mã"
                  >
                    {embedCopied ? (
                      <HiOutlineCheck className="w-4 h-4" />
                    ) : (
                      <HiOutlineClipboardCopy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-800">
                  <strong>Lưu ý:</strong> Website nhúng cần được Admin duyệt trước khi hiển thị map.
                  Liên hệ Admin để đăng ký domain nếu chưa được phép.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowEmbedModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Đóng
              </button>
              <button
                onClick={handleCopyEmbed}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                {embedCopied ? (
                  <>
                    <HiOutlineCheck className="w-4 h-4" />
                    Đã copy!
                  </>
                ) : (
                  <>
                    <HiOutlineClipboardCopy className="w-4 h-4" />
                    Copy mã nhúng
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MapList
